import { GlobalApi } from '@Apis';
import { FormLabel } from '@Components/Common/FormComponent';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '@Models/usePermissionModel';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import SmartTooltip from '@Report/SmartTooltip';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import { calcUnit } from '@Utils/common';
import { IS_UAE } from '@Utils/const';
import { getUtcTime } from '@Utils/time';
import {
  Box,
  Button,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Space,
  Divider,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Text,
  Link,
  ModalOverlay,
  TextLabel,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { CalendarIcon, FileTextOIcon, UserIcon } from '@tonic-one/react-icons';
import i18next from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface MalwareReport {
  DetectionName: string;
  ReportType: string;
  FilePath: string;
  FileName: string;
  FileSize: string;
  FileSHA1: string;
  Confidence: string;
  VirusName: string;
  RelevantDetectionNames: string[];
}

const AddExceptionListModal = ({
  visible,
  onClose,
  fileName,
  fileSha1,
}: {
  visible: boolean;
  onClose: () => void;
  fileName: string;
  fileSha1: string;
}) => {
  const { t } = useTranslation();
  const { widthEditablePermission } = usePermissionStore();
  const modalConfirm = () => {
    try {
      GlobalApi.addPLMExceptionList({
        fileName,
        fileSha1,
      });
      onClose();
      window.showNotification({
        titleMsg: t('globalSetting.PMLExceptions.add.success'),
        severity: 'success',
      });
    } catch (e) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
  };
  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={visible}
      onClose={onClose}
      size="sm"
    >
      <ModalContent>
        <ModalHeader>{t('log.predictiveML.addExListModal.title')}</ModalHeader>
        <ModalBody>
          <Flex direction="column">
            <Flex align="center">
              <FormLabel
                label={t('log.predictiveML.addExListModal.fileName')}
                mr="-28px"
              ></FormLabel>
              <Flex>{fileName}</Flex>
            </Flex>
            <Flex align="center" pr="12px">
              <FormLabel
                label={t('log.predictiveML.tab.fileDetails.sha1')}
                mr="-28px"
              ></FormLabel>
              <Flex width="334px">{fileSha1}</Flex>
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter justifyContent="flex-end">
          <Button
            variant="primary"
            disabled={
              !widthEditablePermission(PermissionNameEnum.administrator)
            }
            onClick={() => modalConfirm()}
            mr="12px"
          >
            {t('common.button.ok')}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ThreatIndicatorsTab = ({ data }: { data: MalwareReport }) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  return (
    <Box>
      <Flex direction="row">
        <Flex direction="column" width="240px">
          <TextLabel mt="8px">
            {t('log.predictiveML.tab.threatIndicators.probability')}
          </TextLabel>
          <Text size="4xl" mt="8px">
            {data.Confidence}%
          </Text>
        </Flex>
        <Flex direction="column">
          <TextLabel mt="8px">
            {t('log.predictiveML.tab.threatIndicators.type')}
          </TextLabel>
          <Text size="4xl" mt="8px">
            {data.VirusName}
          </Text>
        </Flex>
      </Flex>
      <Flex direction="column" mt="24px">
        <Text width="751px" color={colorStyle.color.secondary}>
          {t('log.predictiveML.tab.threatIndicators.desc', {
            param: 'Trend Micro',
          })}
        </Text>
        <Flex mt="8px">
          <Divider ml="12px" variant="solid" orientation="vertical">
            <Flex direction="column" ml="16px">
              <Text
                mb="8px"
                size="sm"
                fontWeight="semibold"
                color={colorStyle.color.primary}
              >
                {t('log.predictiveML.tab.threatIndicators.similarThreats')}
              </Text>
              {data.RelevantDetectionNames &&
                data.RelevantDetectionNames.map((item: any, index: number) => {
                  return (
                    <Text
                      key={index}
                      mt="4px"
                      color={colorStyle.color.secondary}
                    >
                      {item}
                    </Text>
                  );
                })}
            </Flex>
          </Divider>
        </Flex>
      </Flex>
    </Box>
  );
};

const FileDetailsTab = ({ data }: { data: MalwareReport }) => {
  const { t } = useTranslation();
  return (
    <Box>
      <Flex direction="column">
        <Flex direction="column" width="240px">
          <Flex mt="8px" mb="8px">
            {t('log.predictiveML.tab.fileDetails.subTitle')}
          </Flex>
          <Flex align="center">
            <FormLabel
              label={t('log.predictiveML.tab.fileDetails.filename')}
            ></FormLabel>
            <Flex width="580px">
              <SmartTooltip>{data.FileName}</SmartTooltip>
            </Flex>
          </Flex>
          <Flex align="center">
            <FormLabel
              label={t('log.predictiveML.tab.fileDetails.path')}
            ></FormLabel>
            <Flex width="580px">
              <SmartTooltip style={{ whiteSpace: 'nowrap' }}>
                {data.FilePath}
              </SmartTooltip>
            </Flex>
          </Flex>
          <Flex align="center">
            <FormLabel
              label={t('log.predictiveML.tab.fileDetails.size')}
            ></FormLabel>
            <Text>{calcUnit(data.FileSize)}</Text>
          </Flex>
          <Flex align="center">
            <FormLabel
              label={t('log.predictiveML.tab.fileDetails.sha1')}
            ></FormLabel>
            <Text>{data.FileSHA1}</Text>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

const PredictiveMachineLearningLogDetailsTab = ({
  data,
}: {
  data: MalwareReport;
}) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [tabIndex, setTabIndex] = useState(0);
  return (
    <Tabs
      defaultIndex={0}
      index={tabIndex}
      onChange={(index: number) => {
        console.log(index);
        setTabIndex(index);
      }}
    >
      <TabList>
        <Tab
          backgroundColor={
            tabIndex === 0
              ? colorStyle.background.selected
              : colorStyle.background.secondary
          }
        >
          {t('log.predictiveML.tab.threatIndicators.title')}
        </Tab>
        <Tab
          backgroundColor={
            tabIndex === 1
              ? colorStyle.background.selected
              : colorStyle.background.secondary
          }
        >
          {t('log.predictiveML.tab.fileDetails.title')}
        </Tab>
      </TabList>
      <TabPanels
        px="3x"
        py="2x"
        backgroundColor={colorStyle.background.selected}
      >
        <TabPanel>
          <ThreatIndicatorsTab data={data}></ThreatIndicatorsTab>
        </TabPanel>
        <TabPanel>
          <FileDetailsTab data={data}></FileDetailsTab>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

const PredictiveMachineLearningModal = ({
  visible,
  onClose,
  data,
}: {
  visible: boolean;
  onClose: () => void;
  data: any;
}) => {
  const { t } = useTranslation();
  const offsetModel = useUtcOffsetStore();
  const malwareReport = JSON.parse(
    data.malwareReport ? data.malwareReport : '{}'
  );
  malwareReport.FilePath = data.folderPath as string;
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  return (
    <>
      {isAddModalOpen && (
        <AddExceptionListModal
          visible={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          fileName={malwareReport.FileName}
          fileSha1={malwareReport.FileSHA1}
        />
      )}
      <Modal
        ensureFocus={true}
        autoFocus={false}
        closeOnEsc={true}
        closeOnOutsideClick={false}
        isClosable={true}
        isOpen={visible}
        onClose={onClose}
        size="lg"
      >
        <ModalOverlay opacity={0.9} />
        <ModalContent>
          <ModalHeader>{t('log.predictiveML.header')}</ModalHeader>
          <ModalBody>
            <Box mb="8px">
              <Flex>{malwareReport.DetectionName}</Flex>
              <Flex mt="24px">
                <Flex direction="column" width="220px">
                  <Flex align="center">
                    <Icon as={CalendarIcon} />
                    <Space width="2x" />
                    {getUtcTime(
                      data.timestamp,
                      offsetModel.offset,
                      t('common.momentDateFormat')
                    )}
                  </Flex>
                  <Flex mt="12px" ml="24px">
                    {data.action}
                  </Flex>
                </Flex>
                <Divider ml="4px" variant="solid" orientation="vertical" />
                <Flex ml="16px" direction="column" width="220px">
                  <Flex direction="row">
                    <Icon as={FileTextOIcon} />
                    <Space width="8px"></Space>
                    <SmartTooltip>{malwareReport.FileName}</SmartTooltip>
                  </Flex>
                  <Button
                    mt="12px"
                    size="sm"
                    variant="secondary"
                    height="16px"
                    width="160px"
                    onClick={() => setIsAddModalOpen(true)}
                  >
                    {t('log.predictiveML.addExList')}
                  </Button>
                </Flex>
                <Divider ml="4px" variant="solid" orientation="vertical" />
                <Flex ml="16px" width="260px">
                  <Icon as={UserIcon} mr="6px" />
                  <SmartTooltip>{data.violator}</SmartTooltip>
                </Flex>
              </Flex>
            </Box>
            <PredictiveMachineLearningLogDetailsTab
              data={malwareReport}
            ></PredictiveMachineLearningLogDetailsTab>
            <Link
              href={getCasOLHLink(
                CAS_DOC_LINK.policy.atp.predictiveMachineLearning,
                i18next.language
              )}
              mt="12px"
              target="_blank"
            >
              {t('log.predictiveML.link', {
                param: IS_UAE ? '' : 'Trend Micro',
              })}
            </Link>
          </ModalBody>
          <ModalFooter justifyContent="flex-end">
            <Button onClick={onClose} variant="primary">
              {t('common.button.ok')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PredictiveMachineLearningModal;
